'use strict';

/**
 * T451 P3.3 — the BROWSER half of the comms message-store family: open one
 * 🔴 column (a message body or a thread preview) exactly as the server used to
 * open it in message-store.js `_openText`, but under the master key living in
 * the page's vault session — the server never sees the plaintext on this path.
 *
 * Mirrors shared/cores/tasks/task-store-unseal.js (the P3.1 pilot): the key is
 * read from the live vault session (`session._identityMaster`, page-lifetime,
 * never storage) through an injected resolver; the key never leaves this
 * closure; every outcome is TYPED and non-throwing so the client can name it.
 *
 * Delivered via shared/crypto/browser-bundle-entry.js as
 *   window.OysterVault.unsealCommsText(stored, { recordType, recordId })
 *   window.OysterVault.unsealCommsRows(rows, { field, recordType, idField })
 *
 * One stored column is one of FOUR shapes, and the agent classifies it the way
 * `_openText` did — in the same order, for the same reason (a cbox envelope
 * routed into the master-key path would fail-closed and BLANK a sealed inbound
 * body):
 *
 *   { status:'plain', text }     — not JSON / JSON that is no envelope / '' / null:
 *                                  legacy plaintext, passed through unchanged
 *   { status:'cbox',  envelope } — a cbox-v1 seal-box envelope: NOT ours, the
 *                                  vault-kex path (OysterVault.unsealRecord) owns it
 *   { status:'ok',    text }     — a master-key envelope opened under the session key
 *   { status:'locked' }          — a master-key envelope, no unlocked session
 *   { status:'record-binding-mismatch' } — the envelope was sealed for ANOTHER
 *                                  (recordType, recordId): a verbatim-store server
 *                                  cannot swap one of the owner's rows for another
 *                                  undetected. The text is NOT returned.
 *   { status:'sealed-under-a-different-key-or-tampered' } — decrypt refused
 */

const { decryptUnderMasterKey, isMasterKeyEnvelope } = require('../../crypto/master-key-encryption.js');

// seal-box's marker, spelled here rather than required: seal-box.js pulls the
// PQ KEM stack, which this agent must not drag into every consumer.
const CBOX_SEAL_MARKER = 'cbox-v1';

// The seal side (secure-account-store.js `_safeRef`) replaces ':' with '_' in
// both labels before deriving the DEK; the binding check must compare the same
// normalised form or a colon-bearing id would refuse its own row.
function safeRef(value) {
  return String(value == null ? '' : value).replace(/:/g, '_');
}

function createCommsMessageUnsealAgent({ resolveSession } = {}) {
  function masterKey() {
    let session = null;
    try { session = typeof resolveSession === 'function' ? resolveSession() : null; } catch (_e) { session = null; }
    const key = session && session._identityMaster;
    return (key && key.length === 32) ? key : null;
  }

  function unsealCommsText(stored, { recordType, recordId } = {}) {
    if (stored == null || stored === '') return { status: 'plain', text: '' };
    if (typeof stored !== 'string') return { status: 'plain', text: String(stored) };
    let parsed;
    try { parsed = JSON.parse(stored); } catch (_e) { return { status: 'plain', text: stored }; }
    if (!parsed || typeof parsed !== 'object') return { status: 'plain', text: stored };
    if (parsed.seal === CBOX_SEAL_MARKER) return { status: 'cbox', envelope: parsed };
    if (!isMasterKeyEnvelope(parsed)) return { status: 'plain', text: stored };
    // Binding BEFORE the key: a swapped record is refused whether or not the
    // page is unlocked, and refusing never needs the key.
    if (safeRef(parsed.recordType) !== safeRef(recordType) || safeRef(parsed.recordId) !== safeRef(recordId)) {
      return { status: 'record-binding-mismatch' };
    }
    const key = masterKey();
    if (!key) return { status: 'locked' };
    const plain = decryptUnderMasterKey({ envelope: parsed, userMasterKey: key });
    if (plain == null) return { status: 'sealed-under-a-different-key-or-tampered' };
    return { status: 'ok', text: String(plain) };
  }

  /**
   * Open one 🔴 field across a list of rows. Returns shallow COPIES (the input
   * is never mutated): `row[field]` is the text for ok/plain, unchanged for a
   * cbox row (its owner renders it), and '' for every other outcome — no
   * ciphertext ever reaches a render sink; `row[field + 'Unseal']` names the
   * outcome so the UI can offer the unlock. `counts` is for the UI banner.
   */
  function unsealCommsRows(rows, { field, recordType, idField } = {}) {
    const counts = { ok: 0, plain: 0, cbox: 0, locked: 0, failed: 0 };
    const out = [];
    for (const row of Array.isArray(rows) ? rows : []) {
      if (!row || typeof row !== 'object') continue;
      const r = unsealCommsText(row[field], { recordType, recordId: row[idField] });
      const copy = Object.assign({}, row);
      if (r.status === 'ok' || r.status === 'plain') copy[field] = r.text;
      else if (r.status !== 'cbox') copy[field] = '';
      copy[field + 'Unseal'] = r.status;
      if (r.status === 'ok' || r.status === 'plain' || r.status === 'cbox' || r.status === 'locked') counts[r.status] += 1;
      else counts.failed += 1;
      out.push(copy);
    }
    return { rows: out, counts };
  }

  return { unsealCommsText, unsealCommsRows };
}

module.exports = { createCommsMessageUnsealAgent, CBOX_SEAL_MARKER };
